/**
 * BookingCancelDialog.tsx
 * Dialog zum Stornieren einer Buchung mit Rückerstattungs-Optionen
 */

import React, { useState } from 'react';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { Booking, CancellationForm } from '../../types/booking.types';

interface BookingCancelDialogProps {
  booking: Booking | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (cancellation: CancellationForm) => Promise<void>;
}

const BookingCancelDialog: React.FC<BookingCancelDialogProps> = ({
  booking,
  isOpen,
  onClose,
  onConfirm
}) => {
  // State
  const [reason, setReason] = useState('');
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundMethod, setRefundMethod] = useState<'bank' | 'voucher' | 'none'>('bank');
  const [sendConfirmation, setSendConfirmation] = useState(true);
  const [internalNote, setInternalNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens - MUST be before early return
  React.useEffect(() => {
    if (isOpen && booking) {
      setReason('');
      setRefundAmount(booking.paidAmount);
      setRefundMethod('bank');
      setSendConfirmation(true);
      setInternalNote('');
      setConfirmed(false);
      setError(null);
    }
  }, [isOpen, booking]);

  if (!isOpen || !booking) return null;

  // Validierung
  const validateForm = (): boolean => {
    if (reason.trim().length < 10) {
      setError('Grund für Stornierung muss mindestens 10 Zeichen haben');
      return false;
    }
    if (refundAmount < 0 || refundAmount > booking.paidAmount) {
      setError(`Rückerstattungsbetrag muss zwischen €0 und €${booking.paidAmount.toFixed(2)} liegen`);
      return false;
    }
    if (!confirmed) {
      setError('Bitte bestätigen Sie die Stornierung durch Aktivierung der Checkbox');
      return false;
    }
    return true;
  };

  // Handle submit
  const handleSubmit = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const cancellationData: CancellationForm = {
        reason,
        refundAmount,
        refundMethod,
        sendConfirmation,
        internalNote
      };

      await onConfirm(cancellationData);
      onClose();
    } catch (err: any) {
      console.error('Error cancelling booking:', err);
      setError(err.message || 'Fehler beim Stornieren der Buchung');
    } finally {
      setLoading(false);
    }
  };

  // Suggested cancellation reasons
  const suggestedReasons = [
    'Kunde hat storniert - Terminkollision',
    'Kunde hat storniert - Wetterbedenken',
    'Kunde hat storniert - Gruppengröße geändert',
    'Technischer Defekt am Floß',
    'Wetterbedingungen nicht sicher',
    'Doppelbuchung - administrativer Fehler'
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content booking-cancel-dialog"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '600px' }}
      >
        {/* Header */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ExclamationTriangleIcon className="w-6 h-6" style={{ color: '#ff4a4a' }} />
            <h3>Buchung stornieren</h3>
          </div>
          <button onClick={onClose} className="btn-close" disabled={loading}>
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Error Display */}
          {error && (
            <div className="alert alert-error" style={{
              background: 'rgba(255, 74, 74, 0.1)',
              border: '1px solid rgba(255, 74, 74, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: '#ff4a4a'
            }}>
              <ExclamationTriangleIcon className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Booking Summary */}
          <div className="booking-summary" style={{
            background: 'rgba(255, 255, 255, 0.03)',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>Buchungsnummer:</strong> {booking.bookingNumber}
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>Kunde:</strong> {booking.customerName}
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>Termin:</strong> {new Date(booking.tripDate).toLocaleDateString('de-DE')} um {booking.tripTime} Uhr
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>Teilnehmer:</strong> {booking.participantCount} Personen
            </div>
            <div>
              <strong>Gesamtbetrag:</strong> €{booking.totalAmount.toFixed(2)}
              {booking.paidAmount > 0 && ` (€${booking.paidAmount.toFixed(2)} bezahlt)`}
            </div>
          </div>

          {/* Warning */}
          <div className="alert alert-warning" style={{
            background: 'rgba(255, 215, 0, 0.1)',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            color: '#ffd700'
          }}>
            <ExclamationTriangleIcon className="w-5 h-5" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong>Achtung:</strong> Diese Aktion kann nicht rückgängig gemacht werden.
              Die Buchung wird dauerhaft als storniert markiert und die Kapazität wird wieder freigegeben.
            </div>
          </div>

          {/* Reason Section */}
          <div className="form-section" style={{ marginBottom: '20px' }}>
            <label className="form-label" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 600,
              color: '#ffffff'
            }}>
              Stornierungsgrund <span style={{ color: '#ff4a4a' }}>*</span>
            </label>
            <div style={{ marginBottom: '10px' }}>
              <select
                onChange={(e) => setReason(e.target.value)}
                className="filter-select"
                style={{
                  width: '100%',
                  marginBottom: '10px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  padding: '10px',
                  borderRadius: '8px'
                }}
                disabled={loading}
              >
                <option value="">-- Vorlage auswählen (optional) --</option>
                {suggestedReasons.map((r, idx) => (
                  <option key={idx} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Grund für die Stornierung eingeben (mindestens 10 Zeichen)..."
              rows={4}
              className="form-textarea"
              style={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '12px',
                color: 'white',
                fontSize: '14px',
                resize: 'vertical'
              }}
              disabled={loading}
            />
            <div style={{
              fontSize: '12px',
              color: reason.length >= 10 ? '#4ade80' : '#999',
              marginTop: '5px'
            }}>
              {reason.length} / 10 Zeichen (minimum)
            </div>
          </div>

          {/* Refund Section */}
          <div className="form-section" style={{ marginBottom: '20px' }}>
            <label className="form-label" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              fontWeight: 600,
              color: '#ffffff'
            }}>
              <CurrencyEuroIcon className="w-5 h-5" />
              Rückerstattung
            </label>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#999' }}>
                Rückerstattungsbetrag
              </label>
              <input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(parseFloat(e.target.value) || 0)}
                min={0}
                max={booking.paidAmount}
                step={0.01}
                className="form-input"
                style={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  fontSize: '16px'
                }}
                disabled={loading}
              />
              <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                Maximal: €{booking.paidAmount.toFixed(2)} (bereits bezahlter Betrag)
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#999' }}>
                Rückerstattungsmethode
              </label>
              <select
                value={refundMethod}
                onChange={(e) => setRefundMethod(e.target.value as any)}
                className="filter-select"
                style={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  padding: '10px',
                  borderRadius: '8px'
                }}
                disabled={loading || refundAmount === 0}
              >
                <option value="bank">Banküberweisung</option>
                <option value="voucher">Gutschein</option>
                <option value="none">Keine Rückerstattung</option>
              </select>
            </div>
          </div>

          {/* Internal Note */}
          <div className="form-section" style={{ marginBottom: '20px' }}>
            <label className="form-label" style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 600,
              color: '#ffffff'
            }}>
              Interne Notiz (optional)
            </label>
            <textarea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Interne Notizen zur Stornierung (nur für Admins sichtbar)..."
              rows={3}
              className="form-textarea"
              style={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '12px',
                color: 'white',
                fontSize: '14px',
                resize: 'vertical'
              }}
              disabled={loading}
            />
          </div>

          {/* Email Confirmation */}
          <div className="form-section" style={{ marginBottom: '20px' }}>
            <label className="checkbox-label" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={sendConfirmation}
                onChange={(e) => setSendConfirmation(e.target.checked)}
                disabled={loading}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <EnvelopeIcon className="w-5 h-5" />
                Stornierungsbestätigung per E-Mail an Kunde senden
              </span>
            </label>
          </div>

          {/* Confirmation Checkbox */}
          <div className="form-section" style={{ marginBottom: '20px' }}>
            <label className="checkbox-label" style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              cursor: 'pointer',
              background: 'rgba(255, 74, 74, 0.1)',
              border: '1px solid rgba(255, 74, 74, 0.3)',
              borderRadius: '8px',
              padding: '12px'
            }}>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={loading}
                style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
              />
              <span style={{ color: '#ff4a4a', fontSize: '14px' }}>
                <strong>Ich bestätige die Stornierung dieser Buchung.</strong> Mir ist bewusst, dass diese Aktion nicht rückgängig gemacht werden kann.
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{
          display: 'flex',
          gap: '10px',
          padding: '20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
            style={{
              padding: '12px 24px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            className="btn-danger"
            disabled={loading || !confirmed || reason.length < 10}
            style={{
              padding: '12px 24px',
              background: loading || !confirmed || reason.length < 10
                ? 'rgba(255, 74, 74, 0.3)'
                : 'linear-gradient(135deg, #ff4a4a 0%, #cc0000 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: loading || !confirmed || reason.length < 10 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 600
            }}
          >
            {loading ? (
              <>
                <div className="spinner-sm"></div>
                Storniere...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                Buchung stornieren
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingCancelDialog;
